import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Users, Shield } from 'lucide-react';
import { cpApi } from '../api/api';
import VndPageLoading from '../components/VndPageLoading';
import DeleteConfirmationWidget from '../components/DeleteConfirmationWidget';
import type { Counterparty } from '../types';
import { intlLocaleFor } from '../i18n/config';

type TeamPayload = {
  team: { id: number; name: string; responsible_counterparty_id: number };
  members: Array<{ counterparty_id: number; name: string; login: string; salary_balance: number }>;
};

function initials(name: string): string {
  const p = name.trim().split(/\s+/).slice(0, 2);
  return p.map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

export default function VndTeamPage() {
  const { t, i18n } = useTranslation();
  const intlLocale = intlLocaleFor(i18n.language);

  const [data, setData] = useState<TeamPayload | null>(null);
  const [profile, setProfile] = useState<Counterparty | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TeamPayload['members'][0] | null>(null);
  const [editForm, setEditForm] = useState({ name: '', login: '', password: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamPayload['members'][0] | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, p] = await Promise.all([cpApi.getVndTeam(), cpApi.getProfile()]);
      setData(res);
      setProfile(p);
    } catch {
      setError(t('team.loadError'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const h = () => void load();
    window.addEventListener('vndTeamChanged', h);
    return () => window.removeEventListener('vndTeamChanged', h);
  }, [load]);

  useEffect(() => {
    if (!editing) return;
    setEditForm({
      name: editing.name,
      login: editing.login,
      password: '',
    });
  }, [editing]);

  const responsibleId = data?.team?.responsible_counterparty_id;
  const canManage = profile != null && responsibleId != null && profile.id === responsibleId;

  const confirmRemoveMember = async () => {
    if (!memberToRemove) return;
    setRemoveBusy(true);
    setError(null);
    try {
      await cpApi.removeVndTeamMember(memberToRemove.counterparty_id);
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: t('team.removed'), type: 'success' } }));
      setMemberToRemove(null);
      await load();
    } catch {
      setError(t('team.removeError'));
    } finally {
      setRemoveBusy(false);
    }
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    if (!editForm.name.trim() || !editForm.login.trim()) {
      setError(t('team.nameLoginRequired'));
      return;
    }
    setSavingEdit(true);
    setError(null);
    try {
      await cpApi.updateVndTeamMember(editing.counterparty_id, {
        name: editForm.name.trim(),
        login: editForm.login.trim(),
        password: editForm.password.trim() || undefined,
      });
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: t('team.savedEditToast'), type: 'success' } }));
      setEditing(null);
      await load();
    } catch {
      setError(t('team.saveEditError'));
    } finally {
      setSavingEdit(false);
    }
  };

  const fieldClass =
    'w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-100';

  return (
    <div className="min-h-full w-full px-3 pb-6 pt-1 sm:px-4 sm:pt-2">
      {error && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-slate-200/80 bg-white/80 dark:border-slate-700 dark:bg-slate-900/50 overflow-hidden">
          <VndPageLoading fillTable />
        </div>
      ) : !data || data.members.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-white p-10 text-center dark:border-slate-600 dark:from-slate-900/80 dark:to-slate-900/40">
          <Users className="mx-auto h-12 w-12 text-slate-400 dark:text-slate-500" />
          <p className="mt-4 text-base font-medium text-slate-800 dark:text-slate-100">{t('team.emptyTitle')}</p>
          {canManage ? (
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600 dark:text-slate-400">{t('team.emptyHint')}</p>
          ) : null}
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.members.map((m) => {
            const isResponsible = responsibleId === m.counterparty_id;
            const isSelf = profile?.id === m.counterparty_id;
            const showActions = canManage && !isSelf;

            return (
              <li key={m.counterparty_id}>
                <article className="flex h-full flex-col rounded-2xl border border-slate-200/90 bg-white p-5 shadow-lg shadow-slate-200/40 ring-1 ring-slate-100/80 dark:border-slate-700/80 dark:bg-slate-900/60 dark:shadow-black/20 dark:ring-slate-800/60">
                  <div className="flex gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-lg font-bold text-white shadow-inner">
                      {initials(m.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-lg font-semibold text-slate-900 dark:text-white">{m.name}</h2>
                        {isResponsible ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-500/20 dark:text-amber-200">
                            <Shield className="h-3 w-3" />
                            {t('team.responsible')}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 font-mono text-sm text-slate-600 dark:text-slate-400">{m.login}</p>
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">
                        {t('team.balance')}:{' '}
                        <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                          {m.salary_balance.toLocaleString(intlLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                        </span>
                      </p>
                    </div>
                  </div>

                  {showActions ? (
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-slate-700/80">
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setEditing(m);
                        }}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50/50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-emerald-700 dark:hover:bg-emerald-950/30 sm:flex-none"
                      >
                        <Pencil className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        {t('team.editTitle')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setMemberToRemove(m);
                        }}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50/80 px-3 py-2 text-sm font-medium text-red-800 transition hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50 sm:flex-none"
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('common.delete')}
                      </button>
                    </div>
                  ) : isSelf && !canManage ? (
                    <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      {t('team.selfEditHint')}
                    </p>
                  ) : null}
                </article>
              </li>
            );
          })}
        </ul>
      )}

      <DeleteConfirmationWidget
        isOpen={Boolean(memberToRemove)}
        onClose={() => {
          if (removeBusy) return;
          setMemberToRemove(null);
        }}
        onConfirm={() => {
          void confirmRemoveMember();
        }}
        title={t('team.removeTitle')}
        message={
          memberToRemove
            ? t('team.removeMsg', { name: memberToRemove.name || `ID ${memberToRemove.counterparty_id}` })
            : ''
        }
        confirmText={t('team.removeConfirm')}
        confirmLoadingText={t('deleteWidget.processing')}
        isConfirmLoading={removeBusy}
        warningText=""
      />

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('team.editMemberTitle')}</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('team.editMemberSubtitle')}</p>
            <form onSubmit={saveEdit} className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">{t('team.nameLabel')}</label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  className={fieldClass}
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">{t('team.loginLabel')}</label>
                <input
                  value={editForm.login}
                  onChange={(e) => setEditForm((p) => ({ ...p, login: e.target.value }))}
                  className={fieldClass}
                  required
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">{t('team.newPasswordLabel')}</label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm((p) => ({ ...p, password: e.target.value }))}
                  className={fieldClass}
                  placeholder={t('team.passwordKeepPlaceholder')}
                  autoComplete="new-password"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="flex-1 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 py-2.5 text-sm font-semibold text-white shadow-lg disabled:opacity-50"
                >
                  {savingEdit ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
